import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Head from 'next/head'
import { Provider } from 'react-redux'

import { store } from '@store/index'
import { hydrateTokenFromCookie } from '@store/user/actions'
import { setSourceRequest } from '@store/sourceRequest'
import { BrowserThemeProvider } from '@src/shared/wrappers/ThemeProvider'
import { useMountEffect } from '@src/shared/utils/hooks'
import FrontendObservability from '@src/client/grafana'
import ThirdPartyScripts from '@components/ThirdPartyScripts'
import Animate from '@components/Animate'
import { CONTINUE_URL } from '@helpers/urls'
import { LanguageWrapper, tStr } from '@utils/lang'

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter()
  // The old app animated only the /continue/* wizard flow, not the landing page.
  const isContinueFlow = router.pathname.startsWith(CONTINUE_URL)
  const utm = {}
  Object.keys(router.query).forEach(key => {
    if (key.indexOf('utm_') === 0) {
      utm[key.split('utm_')[1]] = router.query[key]
    }
  })
  useMountEffect(() => {
    store.dispatch(
      setSourceRequest({
        ...pageProps.initConsts,
        utm
      })
    )
    store.dispatch(hydrateTokenFromCookie())

    if (pageProps.initConsts?.isPlaywright) {
      window.LOADED = true
    }
  })

  return (
    <>
      <Head>
        <title>{tStr('pageTitle_s')}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=no, viewport-fit=cover" />
      </Head>
      <ThirdPartyScripts />
      <LanguageWrapper>
        <Provider store={store}>
          <FrontendObservability />
          <BrowserThemeProvider>
            {isContinueFlow ? (
              <Animate>
                <Component {...pageProps} />
              </Animate>
            ) : (
              <Component {...pageProps} />
            )}
          </BrowserThemeProvider>
        </Provider>
      </LanguageWrapper>
    </>
  )
}

App.getInitialProps = async ({ ctx }) => {
  const { req } = ctx

  // No req on client-side navigation
  if (!req) {
    return { pageProps: { isProd: process.env.APP_ENV === 'production' } }
  }
  // First load
  const isUpdatingMocks = process.env.UPDATING_MOCKS === 'true'
  const isQA = process.env.DEPLOY_TARGET === 'qa'
  const isLocal = process.env.NODE_ENV !== 'production'
  const isPlaywright = (!!req.get('is-playwright') || isUpdatingMocks) && (isQA || isLocal)

  return {
    pageProps: {
      initConsts: {
        platform: req.phone_type,
        os: req.os,
        referral: req.favorReferral,
        abTestId: req.cookies['ab-test'] ?? '',
        qs: req.originalUrl.split('?')[1] || '',
        isPlaywright,
        coreApi: process.env.CORE_API_HOST,
        //   LAUNCH_DARKLY_CLIENT_ID: process.env.LAUNCH_DARKLY_CLIENT_ID || '',
        FARO_ENDPOINT: process.env.FARO_ENDPOINT || '',
        DEPLOY_TARGET: process.env.DEPLOY_TARGET || ''
      },
      isProd: process.env.APP_ENV === 'production'
    }
  }
}
